import * as yup from 'yup';

import {
    CODE_GENERATOR_COUNTER_DIRECTIONS,
    CODE_GENERATOR_COUNTER_INTERVALS,
    CODE_GENERATOR_MODES,
    CODE_GENERATOR_PART_TYPES,
    CODE_GENERATOR_SUPPORTED_ENTITIES
} from './definitions';

const FIELD_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

const validationSchema = (intl) =>
    yup.object().shape({
        className: yup
            .string()
            .oneOf(
                CODE_GENERATOR_SUPPORTED_ENTITIES.map((option) => option.value),
                intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-classname-unexpectedvalue' })
            )
            .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-classname-required' })),
        data: yup.object().shape({
            applyGenerator: yup.bool().required(),
            targetFieldName: yup
                .string()
                .matches(FIELD_NAME_REGEX, intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-targetfieldname-invalid' }))
                .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-targetfieldname-required' })),
            generatorMode: yup
                .string()
                .oneOf(
                    CODE_GENERATOR_MODES,
                    intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-generatormode-unexpectedvalue' })
                )
                .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-generatormode-required' })),
            pattern: yup.string().when('generatorMode', {
                is: 'pattern_replacement',
                then: (schema) => schema.required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-pattern-required' })),
                otherwise: (schema) => schema.nullable()
            }),
            glue: yup.string().nullable(),
            parts: yup
                .array()
                .of(
                    yup.object().shape({
                        partType: yup
                            .string()
                            .oneOf(
                                CODE_GENERATOR_PART_TYPES,
                                intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-parttype-unexpectedvalue' })
                            )
                            .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-parttype-required' })),
                        value: yup.string().when('partType', {
                            is: 'static_value',
                            then: (schema) =>
                                schema.required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-partvalue-required' })),
                            otherwise: (schema) => schema.nullable()
                        }),
                        direction: yup.string().when('partType', {
                            is: 'counter',
                            then: (schema) =>
                                schema
                                    .oneOf(
                                        CODE_GENERATOR_COUNTER_DIRECTIONS,
                                        intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-direction-unexpectedvalue' })
                                    )
                                    .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-direction-required' })),
                            otherwise: (schema) => schema.nullable()
                        }),
                        interval: yup.string().when('partType', {
                            is: 'counter',
                            then: (schema) =>
                                schema
                                    .oneOf(
                                        CODE_GENERATOR_COUNTER_INTERVALS,
                                        intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-interval-unexpectedvalue' })
                                    )
                                    .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-interval-required' })),
                            otherwise: (schema) => schema.nullable()
                        }),
                        length: yup
                            .mixed()
                            .test(
                                'counter-length',
                                intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-length-invalid' }),
                                function validateLength(value) {
                                    if (this.parent?.partType !== 'counter' || value === null || value === undefined || value === '') {
                                        return true;
                                    }

                                    return Number.isInteger(Number(value)) && Number(value) > 0;
                                }
                            ),
                        format: yup.string().when('partType', {
                            is: 'date',
                            then: (schema) =>
                                schema.required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-dateformat-required' })),
                            otherwise: (schema) => schema.nullable()
                        }),
                        timezone: yup.string().nullable(),
                        fieldName: yup.string().when('partType', {
                            is: (partType) => ['field_value_local', 'field_value_external'].includes(partType),
                            then: (schema) =>
                                schema
                                    .matches(
                                        FIELD_NAME_REGEX,
                                        intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-fieldname-invalid' })
                                    )
                                    .required(intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-fieldname-required' })),
                            otherwise: (schema) => schema.nullable()
                        }),
                        externalEntityFieldName: yup.string().when('partType', {
                            is: 'field_value_external',
                            then: (schema) =>
                                schema
                                    .matches(
                                        FIELD_NAME_REGEX,
                                        intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-externalfieldname-invalid' })
                                    )
                                    .required(
                                        intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-externalfieldname-required' })
                                    ),
                            otherwise: (schema) => schema.nullable()
                        })
                    })
                )
                .min(1, intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-parts-required' }))
                .test(
                    'parts-has-counter',
                    intl.formatMessage({ id: 'ui-comp-formvalidation-codegenerator-parts-missingcounter' }),
                    (parts) => Array.isArray(parts) && parts.some((part) => part?.partType === 'counter')
                )
        })
    });

export default validationSchema;
